import {normalizeDate} from "../utils/date";
import {DisplayType, FlyerItemJSON, ItemJSON, TrackingUrlJSON} from "./BackflippDefinitions";
import {Category} from "./CommonDefinitions";
import {https} from "../utils/http";

export class FlyerCategory extends Category {}

export class Flyer {
	availableFrom: Date;
	availableTo: Date;
	budgetId: number;
	categories: string[];
	displayType: string;
	flyerRunId: number;
	flyerTypeId: number;
	height: number;
	id: number;
	merchant: string;
	merchantId: number;
	merchantLogo: string;
	name: string;
	path: string;
	postalCode: string;
	premium: boolean;
	priority: number;
	resolutions: number[];
	thumbnail: string;
	thumbnailPremium: string;
	validFrom: Date;
	validTo: Date;
	webIndexed: boolean;
	width: number;
	isStoreSelect: boolean;
	isTombstone: boolean;
	items: FlyerItem[];
	details: FlyerDetails;
	analyticsPayload: string;
	_detailsPromise: Promise<FlyerDetails>;
	trackingUrls: TrackingUrl[];
	city_state: string|null;

	constructor(json: any) {
		this.availableFrom = normalizeDate(json.available_from);
		this.availableTo = normalizeDate(json.available_to, true);
		this.budgetId = json.budget_id;
		this.categories = json.categories;
		this.flyerRunId = json.flyer_run_id;
		this.flyerTypeId = json.flyer_type_id;
		this.height = json.height;
		this.id = json.id;
		this.merchant = json.merchant;
		this.merchantId = json.merchant_id;
		this.merchantLogo = json.merchant_logo;
		this.name = json.name;
		this.path = json.path;
		this.postalCode = json.postal_code;
		this.premium = json.premium;
		this.priority = json.priority;
		this.resolutions = json.resolutions;
		this.thumbnail = https(json.thumbnail_url);
		this.thumbnailPremium = https(json.premium_thumbnail_url);
		this.validFrom = normalizeDate(json.valid_from);
		this.validTo = normalizeDate(json.valid_to, true);
		this.webIndexed = json.web_indexed;
		this.width = json.width;
		this.isStoreSelect = json.is_store_select;
		this.isTombstone = false;
		this.displayType = json.display_type;
		this.analyticsPayload = json.analytics_payload;
		this.trackingUrls = json.tracking_urls ? json.tracking_urls.map((tracking_url_json: TrackingUrlJSON) =>
			new TrackingUrl(tracking_url_json)
		) : [];
		this.city_state = json.city_state;
	}

	getName(): string {
		return `${this.merchant} ${this.name}`;
	}
}

export class TrackingUrl {
	eventType: number;
	url: string;

	constructor(json: TrackingUrlJSON) {
		this.eventType = json.event_type;
		this.url = json.url;
	}
}

export class FlyerDetails {
	items: FlyerItem[];
	hasCorrections: boolean;
	pages: {
		id: number;
		page: number;
		name: string;
		top: number;
		left: number;
		bottom: number;
		right: number;
	}[];

	// If a special parameter is passed to the /flipp/flyers/<id> endpoint, then flyer object will be returned.
	flyer?: Flyer;

	constructor(json: any) {
		this.hasCorrections = json.has_corrections;
		this.pages = json.pages;
		const flyerName = json.flyer && json.flyer.name ? json.flyer.name : "";
		const merchantName = json.flyer && json.flyer.merchant ? json.flyer.merchant : "";
		this.items = json.items.map((itemJson: any) =>
				new FlyerItem(itemJson, flyerName, merchantName));
		if (json.flyer) {
			this.flyer = new Flyer(json.flyer);
		}
	}
}

export class FlyerItem {
	id: number;
	flyerId: number;
	shortName: string;
	brand: string;
	name: string;
	price: string;
	displayType: DisplayType;
	discount: number;
	left: number;
	bottom: number;
	right: number;
	top: number;
	validFrom: string;
	validTo: string;
	availableTo: string;
	videoUrl: string;
	textAreas: {
		id: number,
		left: number,
		bottom: number,
		right: number,
		top: number
	}[];
	cutoutImageUrl: string;
	pageDestination: number;
	printId: string;

	json: FlyerItemJSON;
	flyerName: string;
	merchantName: string;

	constructor(json: FlyerItemJSON, flyerName: string, merchantName: string) {
		this.id = json.id;
		this.flyerId = json.flyer_id;
		this.shortName = json.short_name;
		this.brand = json.brand;
		this.name = json.name;
		this.price = json.price;
		this.displayType = json.display_type;
		this.discount = json.discount;
		this.left = json.left;
		this.bottom = json.bottom;
		this.right = json.right;
		this.top = json.top;
		this.validFrom = json.valid_from;
		this.validTo = json.valid_to;
		this.availableTo = json.available_to;
		this.videoUrl = json.video_url;
		this.textAreas = json.text_areas;
		this.cutoutImageUrl = json.cutout_image_url;
		this.pageDestination = json.page_destination;
		this.printId = json.print_id;
		this.json = json;
		this.flyerName = flyerName;
		this.merchantName = merchantName;
	}
}

export class FlyerItemDetails {
	flyerRunId: number;
	id: number;
	flyerId: number;
	brand: string;
	displayType: DisplayType;
	merchant: string;
	merchantLogo: string;
	name: string;
	imageUrl: string;
	cutoutImageUrl: string;
	description: string;
	currentPrice: string;
	currentPriceRange: string; //
	prePriceText: string;
	category: string;
	priceText: string;
	saleStory: string;
	originalPrice: string;
	originalPriceRange: string; //
	inStoreOnly: boolean;
	disclaimerText: string;
	flyerDisclaimerText: string; //

	validFrom: Date;
	validTo: Date;

	timezone: number; //
	sku: string;
	ttmLabel: string;
	ttmUrl: string;
	showDetails: boolean;
	reviewAverageRating: number;
	reviewSamples: {
		body: string;
		byline: string;
		date: string;
		rating: number;
		title: string;
	}[];
	specs: {
		name: string;
		value: string;
	}[];
	features: {
		text: string;
	}[];

	// url: string;
	// webIndexed: boolean;
	// videoUrl: string;

	constructor(json: ItemJSON) {
		this.brand = json.item.brand;
		this.category = json.item.category;
		this.currentPrice = json.item.current_price;
		this.cutoutImageUrl = json.item.cutout_image_url;
		this.description = json.item.description;
		this.disclaimerText = json.item.disclaimer_text;
		this.flyerDisclaimerText = json.item.flyer_disclaimer_text;
		this.displayType = json.item.display_type;
		this.features = json.item.features;
		this.flyerId = json.item.flyer_id;
		this.id = json.item.id;
		this.flyerRunId = json.item.flyerRunId;
		this.imageUrl = json.item.image_url;
		this.inStoreOnly = json.item.in_store_only;
		this.merchant = json.item.merchant;
		this.merchantLogo = json.item.merchant_logo;
		this.name = json.item.name;
		this.originalPrice = json.item.original_price;
		this.prePriceText = json.item.pre_price_text;
		this.priceText = json.item.price_text;
		this.reviewAverageRating = json.item.review_average_rating;
		this.reviewSamples = json.item.review_samples;
		this.saleStory = json.item.sale_story;
		this.showDetails = json.item.show_details;
		this.sku = json.item.sku;
		this.specs = json.item.specs;
		this.ttmLabel = json.item.ttm_label;
		this.ttmUrl = json.item.ttm_url;
		this.validFrom = json.item.valid_from;
		this.validTo = json.item.valid_to;

		// this.url = json.item.url;
		// this.webIndexed = json.item.web_indexed;
		// this.videoUrl = json.item.video_url;
		// this.iframeDisplayWidth = json.item.iframe_display_width;
		// this.iframeDisplayHeight = json.item.iframe_display_height;
	}
}

export class Store {
	id: number;
	name: string;
	logo: string;
}
